import { isEqual } from 'lodash';
import { useCallback, useEffect, useState } from 'react';

import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import Container from '@mui/material/Container';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';

import { getPaginatedAudits } from 'src/redux/slices/audit';
import { useAppDispatcher, useAppSelector } from 'src/redux/store';

import Scrollbar from 'src/components/scrollbar';
import { useSettingsContext } from 'src/components/settings';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import TableErrorData from 'src/components/table/table-error-data';
import {
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  TableSkeleton,
  useTable,
} from 'src/components/table';

import { IAuditListItem, IAuditTableFilters, IAuditTableFilterValue, IAuditTableSort } from 'src/types/audit';

import AuditLogTableRow from '../audit-log-table-row';
import AuditLogTableToolbar from '../audit-log-table-toolbar';

const TABLE_HEAD = [
  { id: 'type', label: 'Type', align: 'left', primary: true },
  { id: 'action', label: 'Action', align: 'left', primary: true },
  { id: 'actorEmail', label: 'Actor', align: 'left', primary: true },
  { id: 'createdAt', label: 'Date', align: 'left', primary: true },
  { id: '', primary: true },
];

const defaultFilters: IAuditTableFilters = {
  generalSearch: '',
  type: '',
  action: '',
  actorId: '',
  startDate: null,
  endDate: null,
};

const defaultSortBy: IAuditTableSort = {
  sortBy: 'createdAt',
  order: 'desc',
};

export default function AuditLogListView() {
  const { status, audits, error } = useAppSelector((state) => state.audit);

  const table = useTable({
    defaultRowsPerPage: 25,
  });

  const dispatch = useAppDispatcher();

  const { page, rowsPerPage } = table;

  const settings = useSettingsContext();

  const denseHeight = table.dense ? 60 : 80;

  const [filters, setFilters] = useState(defaultFilters);

  const [sort, setSort] = useState(defaultSortBy);

  const canReset = !isEqual(defaultFilters, filters);

  const handleFilters = useCallback(
    (name: string, value: IAuditTableFilterValue) => {
      table.onResetPage();

      setFilters((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    },
    [table]
  );

  const handleResetFilters = useCallback(() => {
    setFilters(defaultFilters);
  }, []);

  const fetchPaginatedAudits = useCallback(() => {
    dispatch(
      getPaginatedAudits({
        page,
        limit: rowsPerPage,
        generalSearch: filters.generalSearch,
        type: filters.type,
        action: filters.action,
        actorId: filters.actorId,
        startDate: filters.startDate,
        endDate: filters.endDate,
        sort: sort.order,
        sortBy: sort.sortBy,
      })
    );
  }, [dispatch, page, rowsPerPage, sort.order, sort.sortBy, filters]);

  useEffect(() => {
    fetchPaginatedAudits();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPaginatedAudits]);

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <CustomBreadcrumbs
        heading="Audit Log"
        links={[]}
        sx={{
          mb: { xs: 3 },
        }}
      />

      <Card>
        <AuditLogTableToolbar
          filters={filters}
          onFilters={handleFilters}
          handleResetFilters={handleResetFilters}
          canReset={canReset}
        />

        <TableContainer sx={{ position: 'relative', overflow: 'hidden', height: '450px' }}>
          <Scrollbar>
            <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 960 }}>
              <TableHeadCustom
                order={sort.order}
                orderBy={sort.sortBy}
                headLabel={TABLE_HEAD.filter(({ primary }) => {
                  if (settings.themeLayout === 'vertical') {
                    return primary;
                  }
                  return true;
                })}
                rowCount={audits.items.length}
                numSelected={table.selected.length}
                onSort={(id) => {
                  if (id === sort.sortBy) {
                    setSort((prev) => ({
                      ...prev,
                      order: prev.order === 'asc' ? 'desc' : 'asc',
                    }));
                  } else {
                    setSort({
                      sortBy: id,
                      order: 'asc',
                    });
                  }
                }}
              />

              <TableBody>
                {status === 'loading' ? (
                  [...Array(table.rowsPerPage)].map((i, index) => (
                    <TableSkeleton key={index} sx={{ height: denseHeight }} />
                  ))
                ) : (
                  <>
                    {audits?.items.map((row: IAuditListItem) => (
                      <AuditLogTableRow key={row._id} row={row} />
                    ))}
                  </>
                )}

                {status !== 'failure' && audits.count === 0 && (
                  <TableNoData message="No Audit Log Entries Found" notFound={audits.count === 0} />
                )}

                {status === 'failure' && <TableErrorData notFound error={error} />}
              </TableBody>
            </Table>
          </Scrollbar>
        </TableContainer>

        <TablePaginationCustom
          count={audits.count}
          page={table.page}
          rowsPerPage={table.rowsPerPage}
          onPageChange={table.onChangePage}
          onRowsPerPageChange={table.onChangeRowsPerPage}
          dense={table.dense}
          onChangeDense={(value) => {
            table.onChangeDense(value);
          }}
        />
      </Card>
    </Container>
  );
}
